import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Category, CreateCategoryRequest, UpdateCategoryRequest } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { errorMessage } from '../../utils/errorMessage';

interface CategoryFormProps {
  category?: Category | null;
  onSubmit: (data: CreateCategoryRequest | UpdateCategoryRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  category,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#397159',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isEditing = Boolean(category);
  const colorChoices = ['#397159', '#35718E', '#B78638', '#9A5955', '#6E627A'];

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        color: category.color || '#397159',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: '#397159',
      });
    }
    setErrors({});
    setSubmitError(null);
  }, [category]);

  const handleChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('category.nameRequired');
    }

    if (formData.color && !formData.color.match(/^#[0-9A-Fa-f]{6}$/)) {
      newErrors.color = t('category.colorInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitError(null);
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        color: formData.color || undefined,
      };
      await onSubmit(submitData);
    } catch (error: unknown) {
      setSubmitError(errorMessage(error, t('category.failedToSave')));
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Stack spacing={3}>
        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <TextField
          fullWidth
          label={t('category.name')}
          value={formData.name}
          onChange={handleChange('name')}
          error={!!errors.name}
          helperText={errors.name}
          disabled={isLoading}
          required
        />

        <TextField
          fullWidth
          label={t('category.description')}
          value={formData.description}
          onChange={handleChange('description')}
          error={!!errors.description}
          helperText={errors.description}
          disabled={isLoading}
          multiline
          rows={3}
        />

        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {t('category.color')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {colorChoices.map(color => <Button key={color} aria-label={`${t('category.color')} ${color}`} aria-pressed={formData.color.toLowerCase() === color.toLowerCase()} onClick={() => setFormData(previous => ({ ...previous, color }))} disabled={isLoading} sx={{ minWidth: 38, width: 38, height: 38, borderRadius: 1, bgcolor: color, border: formData.color.toLowerCase() === color.toLowerCase() ? '3px solid' : '1px solid', borderColor: formData.color.toLowerCase() === color.toLowerCase() ? 'text.primary' : 'divider', '&:hover': { bgcolor: color, opacity: .85 } }} />)}
            <TextField type="color" value={formData.color} onChange={handleChange('color')} error={!!errors.color} helperText={errors.color} disabled={isLoading} sx={{ width: 74, '& input': { p: .5, height: 38 } }} inputProps={{ 'aria-label': t('category.color') }} />
          </Stack>
        </Box>

        <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {isEditing ? t('common.update') : t('common.create')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CategoryForm;
